import React, { useCallback } from 'react';
import { Platform } from 'react-native';
import { HeaderTitle } from '@react-navigation/elements';
import { useRouter } from 'expo-router';
import { useTranslation } from '@/hooks/useTranslation';
import NavigationBarButtonContainer from '@/components/Navigation/NavigationBarButtonContainer';
import NavigationBarButton from '@/components/Navigation/NavigationBarButton';
import useAndroidDetailBack from '@/hooks/useAndroidDetailBack';
import ToolPresetsDropdown from '@/components/Dropdowns/ToolPresetsDropdown';
import ToolRunsDropdown from '@/components/Dropdowns/ToolRunsDropdown';
import { useAuthStore } from '@/store';

// Tool detail page header is data-driven; we derive the title from the tool
// object pulled out of the store at navigation time. The right side hosts two
// quick-access dropdowns:
//   - Presets (lightbulb)        — pre-fill the form with a saved example
//   - Recent runs (clock-rotate) — switch the visible result, jump to history
// Both dropdowns subscribe to the tool store and communicate back to the page
// via the `requestApplyExample` / `requestActiveRun` bridge.

const ToolDetailNavigationHeaderLeft = React.memo(() => {
  const router = useRouter();
  const { t } = useTranslation();
  useAndroidDetailBack(router, '/tools');

  const onBack = useCallback(() => {
    if (Platform.OS === 'web' && router.canGoBack?.()) {
      router.back();
      return;
    }
    router.replace('/tools');
  }, [router]);

  return (
    <NavigationBarButtonContainer leftPadding>
      <NavigationBarButton
        icon="chevron-left"
        tooltip={t('nav.routes.tools.backToToolsTitle')}
        accessibilityLabel={t('nav.routes.tools.backToTools')}
        onPress={onBack}
      />
    </NavigationBarButtonContainer>
  );
});

ToolDetailNavigationHeaderLeft.displayName = 'ToolDetailNavigationHeaderLeft';

const ToolDetailRouteNavigationHeaderRight = React.memo(({ slug }) => {
  const hasUser = useAuthStore(state => Boolean(state.user?.id));
  // Presets and recent runs are per-user; hide both for logged-out visitors.
  if (!slug || !hasUser) return null;
  return (
    <NavigationBarButtonContainer rightPadding>
      <ToolPresetsDropdown slug={slug} />
      <ToolRunsDropdown slug={slug} />
    </NavigationBarButtonContainer>
  );
});

ToolDetailRouteNavigationHeaderRight.displayName = 'ToolDetailRouteNavigationHeaderRight';

function getToolDetailRouteNavigationOptions(tool, slug) {
  const title = tool?.name || 'Tool';
  return {
    title,
    headerTitle: Platform.OS === 'web'
      ? props => <HeaderTitle {...props} role={undefined} aria-level={undefined} />
      : title,
    headerBackVisible: false,
    headerLeft: () => <ToolDetailNavigationHeaderLeft />,
    headerRight: () => <ToolDetailRouteNavigationHeaderRight slug={slug} />,
  };
}

export default getToolDetailRouteNavigationOptions;
